/**
 * Cloning an edition's structure (fase 2, steg 2 — third delivery).
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT A CLONE COPIES: STRUCTURE, NEVER DATA.
 *
 * The build plan asks for "kloning av struktur fra forrige edition", and the reason a festival
 * needs it is that a year should not start from nothing. What that means concretely was worth
 * checking rather than assuming: four tables carry `edition_id` — `departments`, `tasks`,
 * `threads` and `edition_roles` — and only the first is STRUCTURE. Last year's tasks and
 * correspondence belong to last year. Copying them forward would be forging a record.
 *
 * So today a clone copies the department tree, and nothing else. Stages and accreditation levels
 * will join it when they exist, which is why the receipt below is an object keyed by what was
 * copied rather than a single number: adding a kind is then an addition, not a change.
 *
 * WHERE IT COPIES FROM, AND WHY THAT IS TWO ANSWERS. The plan says "fra forrige edition", but no
 * edition has a tree of its own until one is cloned into it, so the first clone of every festival
 * would copy nothing. The organisation's own tree — `departments` with `edition_id is null`, the
 * one Settings administers — is the template a first edition starts from; a previous edition is
 * the natural source after that. Atle's call, 28 August.
 *
 * No schema change was needed for either: `ux_departments__scope_key` is
 * `(organization_id, edition_id, key)` with `nullsNotDistinct`, so the organisation's tree and
 * every edition's own tree already coexist, and `key` is what links a department to its copy.
 *
 * `cloned_from_edition_id` IS SET ONLY WHEN THE SOURCE IS AN EDITION. Cloning from the
 * organisation's template is not "cloned from" anything the column can point at, and writing the
 * new edition's own id there to have something to write would be a lie in a record whose whole
 * job is to say where this year came from.
 *
 * RUNNING IT TWICE IS SAFE AND SAYS SO. A key that already exists in the target is skipped, not
 * overwritten and not duplicated — the unique index would refuse a duplicate anyway, and failing
 * halfway through a copy is the worst of the three outcomes. The receipt separates `created` from
 * `skipped` so a second run reports "0 created, 19 already there" rather than looking like it did
 * nothing for an unknown reason.
 * ---------------------------------------------------------------------------------------------
 */
import { z } from 'zod';

import { editionDtoSchema } from './editions.js';

/**
 * Where the structure comes from.
 *
 * A discriminated union rather than an optional `fromEditionId` where absent means the
 * organisation: "absent means the org template" is a rule the caller has to know, and a caller
 * that forgot to send the id would silently get the wrong source instead of a 400.
 */
export const cloneSourceSchema = z
  .discriminatedUnion('from', [
    z.object({
      from: z.literal('organization'),
    }),
    z.object({
      from: z.literal('edition'),
      /** Must belong to the same festival. A structure from another event is not a template. */
      editionId: z.uuid(),
    }),
  ])
  .meta({ id: 'CloneSource' });

export const cloneEditionRequestSchema = z
  .object({
    source: cloneSourceSchema,
  })
  .meta({ id: 'CloneEditionRequest' });

/**
 * What the clone did, per kind of thing.
 *
 * Keyed by kind so stages and accreditation levels are added rather than substituted, and split
 * into created and skipped so a second run is legible instead of merely quiet.
 */
export const cloneCountsSchema = z
  .object({
    created: z.number().int().nonnegative(),
    /** Already present in the target under the same key, and therefore left alone. */
    skipped: z.number().int().nonnegative(),
  })
  .meta({ id: 'CloneCounts' });

export const cloneEditionResponseSchema = z
  .object({
    /** The target edition as it now stands, including `clonedFromEditionId` when one was set. */
    edition: editionDtoSchema,
    copied: z
      .object({
        departments: cloneCountsSchema,
        /**
         * Added 30 August (sak 50), which is the addition this shape was keyed by kind FOR.
         *
         * Where they come from depends on the source: an edition source copies last year's stages,
         * the organisation source copies the festival's template. Most runs report `created: 0`,
         * because creating the year already stamped the template into it — what a clone adds is
         * whatever the source year had on top.
         */
        stages: cloneCountsSchema,
      })
      .meta({ id: 'CloneCopied' }),
  })
  .meta({ id: 'CloneEditionResponse' });

export type CloneSource = z.infer<typeof cloneSourceSchema>;
export type CloneEditionRequest = z.infer<typeof cloneEditionRequestSchema>;
export type CloneCounts = z.infer<typeof cloneCountsSchema>;
export type CloneEditionResponse = z.infer<typeof cloneEditionResponseSchema>;
